// TypeScript interfaces for SortingCat AI file queue and job management

export interface FileValidation {
  isValid: boolean;
  reason?: 'size_exceeded' | 'unsupported_format';
  maxSize?: number;
}

export interface QueuedFile {
  id: string;
  file: File;
  addedAt: Date;
  type: 'image' | 'archive' | 'unknown';
  size: number;
  validation: FileValidation;
}

export interface JobStatus {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'error' | 'failed';
  progress?: number;
  message?: string;
  createdAt: Date;
  completedAt?: Date;
  fileCount: number;
  processedFiles?: number; // Added for Convex migration
  failedFiles?: number; // Added for Convex migration
  results?: {
    download_url?: string;
    filename?: string;
    size?: number;
    metadata?: {
      totalFiles: number;
      processingTime: number;
      organizationSummary: {
        foldersCreated: number;
        filesOrganized: number;
      };
    };
  };
}

export interface AppState {
  fileQueue: QueuedFile[];
  currentJob: JobStatus | null;
  isSubmitting: boolean;
  error: string | null;
  lastUpdated: Date;
  isEmailValid: boolean;
  email: string;
  categoryMode: 'predefined' | 'custom' | 'hybrid';
  customCategories: string[];
  areCategoriesValid: boolean;
}

export type FileQueueAction =
  | { type: 'ADD_FILES'; payload: File[] }
  | { type: 'REMOVE_FILE'; payload: string } // file id
  | { type: 'CLEAR_QUEUE' }
  | { type: 'SET_JOB_STATUS'; payload: JobStatus }
  | { type: 'SET_SUBMITTING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_JOB' }
  | { type: 'SET_EMAIL'; payload: { email: string; isValid: boolean } }
  | { type: 'SET_CATEGORY_MODE'; payload: 'predefined' | 'custom' | 'hybrid' }
  | { type: 'SET_CUSTOM_CATEGORIES'; payload: { categories: string[]; isValid: boolean } };

// Category-related types
export type CategoryMode = 'predefined' | 'custom' | 'hybrid';

export interface CategoryInputProps {
  onCategoriesChange: (categories: string[], mode: CategoryMode, isValid: boolean) => void;
  initialMode?: CategoryMode;
  initialCategories?: string[];
  required?: boolean;
}

export interface CustomCategoriesData {
  categories: string[];
  instructions?: string;
  fallbackToPredefined?: boolean;
  classificationMode?: 'color' | 'content' | 'semantic' | 'mixed';
}

// Utility functions
export const getFileType = (file: File): 'image' | 'archive' | 'unknown' => {
  if (file.type.startsWith('image/') || 
      file.name.toLowerCase().endsWith('.heic') || 
      file.name.toLowerCase().endsWith('.heif')) {
    return 'image';
  }
  if (file.type === 'application/zip' || 
      file.type === 'application/x-zip-compressed' ||
      file.name.toLowerCase().endsWith('.zip')) {
    return 'archive';
  }
  return 'unknown';
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))  } ${  sizes[i]}`;
};

export const generateFileId = (): string => {
  return `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Category validation utilities
export const validateCategoryName = (name: string): boolean => {
  if (!name || name.trim().length < 2) return false;
  if (name.length > 50) return false;
  if (!/^[a-zA-Z0-9\s\-_]+$/.test(name)) return false;
  return true;
};

export const formatCategoryName = (name: string): string => {
  return name.trim().toLowerCase().replace(/\s+/g, '-');
};